/**
 * Tracking Manager
 * Controls enabling/disabling tracking scripts based on privacy opt-out status
 * Works in conjunction with PrivacyOptOut module
 */
(() => {
  'use strict';

  const TrackingManager = {
    isInitialized: false,
    trackingEnabled: true,
    blockedScripts: [],

    /**
     * Initialize the tracking manager
     */
    init() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      // Honor Shopify's native Customer Privacy API (region-aware, category-aware).
      // This reacts to the native cookie-consent banner -- analytics vs. marketing --
      // in addition to the custom CCPA opt-out flow below.
      this.initNativeConsent();

      // Listen for privacy opt-out ready event
      document.addEventListener('privacyOptOutReady', (e) => {
        this.handlePrivacyStatus(e.detail);
      });

      // Listen for privacy opt-out changes
      document.addEventListener('privacyOptOutChange', (e) => {
        this.handlePrivacyChange(e.detail);
      });

      // Check if PrivacyOptOut already initialized
      if (window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut !== undefined) {
        this.handlePrivacyStatus({
          isOptedOut: window.PrivacyOptOut.isOptedOut,
          hasGPCSignal: window.PrivacyOptOut.hasGPCSignal
        });
      }

      // Fallback: check localStorage directly (for pages without PrivacyOptOut module)
      if (!window.PrivacyOptOut) {
        try {
          const stored = localStorage.getItem('privacy_opt_out');
          if (stored) {
            const data = JSON.parse(stored);
            if (data.optedOut) {
              this.handlePrivacyStatus({ isOptedOut: true, hasGPCSignal: !!data.gpcDetected });
            }
          }
        } catch (e) {
          console.error('[TrackingManager] Error reading localStorage fallback:', e);
        }
      }

    },

    /**
     * Handle initial privacy status
     * @param {Object} detail - Privacy status details
     */
    handlePrivacyStatus(detail) {
      this.trackingEnabled = !detail.isOptedOut;

      if (detail.isOptedOut) {
        this.disableTracking();
      } else {
        this.enableTracking();
      }

    },

    /**
     * Handle privacy status change
     * @param {Object} detail - Change details
     */
    handlePrivacyChange(detail) {
      this.trackingEnabled = !detail.isOptedOut;

      if (detail.isOptedOut) {
        this.disableTracking();
      } else {
        this.enableTracking();
      }

    },

    /**
     * Subscribe to Shopify's native Customer Privacy API.
     *
     * `visitorConsentCollected` is a DOM event, so we can listen for it even
     * before the API object finishes loading. We also do an initial read once
     * the API is available so trackers are gated correctly on first paint.
     *
     * Region model comes for free: analyticsProcessingAllowed()/marketingAllowed()
     * return "allowed" by default in opt-out regions (US/CCPA) and
     * "blocked until consent" in opt-in regions (EU/GDPR).
     */
    initNativeConsent() {
      // Re-evaluate whenever the visitor accepts/declines/changes their choice.
      document.addEventListener('visitorConsentCollected', () => {
        this.evaluateNativeConsent();
      });

      this.whenNativeApiReady(() => this.evaluateNativeConsent());
    },

    /**
     * Wait for window.Shopify.customerPrivacy to load, then run cb.
     * No-op (silently) if the native banner isn't enabled in admin.
     * @param {Function} cb
     * @param {number} retryCount
     */
    whenNativeApiReady(cb, retryCount = 0) {
      const MAX_RETRIES = 5;
      const RETRY_DELAY = 500; // ms

      if (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) {
        cb();
      } else if (retryCount < MAX_RETRIES) {
        setTimeout(() => this.whenNativeApiReady(cb, retryCount + 1), RETRY_DELAY * (retryCount + 1));
      }
    },

    /**
     * Read the native consent decision and gate trackers per category.
     * Analytics consent gates GA; marketing consent gates ad pixels (Meta, Ads).
     */
    evaluateNativeConsent() {
      const cp = (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) || null;
      if (!cp) return;

      // Default to allowed if a method is missing so we never over-block in
      // opt-out regions where tracking is permitted by default.
      const analyticsAllowed = typeof cp.analyticsProcessingAllowed === 'function'
        ? cp.analyticsProcessingAllowed() !== false
        : true;
      const marketingAllowed = typeof cp.marketingAllowed === 'function'
        ? cp.marketingAllowed() !== false
        : true;

      if (!analyticsAllowed) {
        this.disableGoogleAnalytics();
        this.disableYotpoTracking();
        this.disableWiedersehen();
      }

      if (!marketingAllowed) {
        this.disableFacebookPixel();
        // Deny Google Ads storage signals via Consent Mode if gtag is present.
        if (typeof window.gtag === 'function') {
          window.gtag('consent', 'update', {
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied'
          });
        }
      }

      // If either category is blocked, set the global flag so ad-hoc tracking
      // calls guarded by TrackingManager.track()/isTrackingEnabled() also stop.
      if (!analyticsAllowed || !marketingAllowed) {
        this.trackingEnabled = false;
      }
    },

    /**
     * Disable all tracking scripts
     */
    disableTracking() {
      // Disable Google Analytics
      this.disableGoogleAnalytics();

      // Disable Facebook Pixel
      this.disableFacebookPixel();

      // Disable Yotpo tracking (but keep reviews functional)
      this.disableYotpoTracking();

      // Disable wiedersehen analytics
      this.disableWiedersehen();

      // Set global flag for other scripts to check
      window.__trackingDisabled = true;

      // Clear any existing tracking cookies
      this.clearTrackingCookies();
    },

    /**
     * Enable tracking (only works if not opted out)
     */
    enableTracking() {
      if (window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut) {
        return;
      }

      window.__trackingDisabled = false;
    },

    /**
     * Disable Google Analytics
     */
    disableGoogleAnalytics() {
      // Disable GA4
      window['ga-disable-' + (window.gaTrackingId || '')] = true;

      // Opt out of GA
      if (typeof window.gaOptout === 'function') {
        window.gaOptout();
      }

      // Disable gtag
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      }

      // Prevent future GA calls
      window.ga = function() {};
    },

    /**
     * Disable Facebook Pixel
     */
    disableFacebookPixel() {
      // Disable fbq
      if (typeof window.fbq === 'function') {
        window.fbq('consent', 'revoke');
      }

      // Replace fbq with no-op
      window.fbq = function() {};
    },

    /**
     * Disable Yotpo tracking features (keep reviews working)
     */
    disableYotpoTracking() {
      if (window.yotpo) {
        // Disable conversion tracking but keep review display
        window.yotpo.disableConversionTracking = true;
      }

    },

    /**
     * Disable wiedersehen analytics
     */
    disableWiedersehen() {
      // Set flag to prevent wiedersehen from sending data
      window.__wiedersehenDisabled = true;

      // If wiedersehen is already loaded, try to disable it
      if (window.wiedersehen && typeof window.wiedersehen.disable === 'function') {
        window.wiedersehen.disable();
      }

    },

    /**
     * Clear tracking-related cookies
     */
    clearTrackingCookies() {
      const trackingCookies = [
        '_ga',
        '_gid',
        '_gat',
        '_fbp',
        '_fbc',
        'fr',
        '_gcl_au'
      ];

      trackingCookies.forEach(cookieName => {
        this.deleteCookie(cookieName);
      });

    },

    /**
     * Delete a cookie by name
     * @param {string} name - Cookie name
     */
    deleteCookie(name) {
      const domains = [
        window.location.hostname,
        '.' + window.location.hostname,
        window.location.hostname.replace(/^www\./, '.'),
        '.' + window.location.hostname.replace(/^www\./, '')
      ];

      domains.forEach(domain => {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=' + domain;
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
      });
    },

    /**
     * Check if tracking is currently enabled
     * @returns {boolean}
     */
    isTrackingEnabled() {
      return this.trackingEnabled && !window.__trackingDisabled;
    },

    /**
     * Wrapper for conditional tracking calls
     * @param {Function} trackingFn - Function to call if tracking is enabled
     */
    track(trackingFn) {
      if (this.isTrackingEnabled()) {
        try {
          trackingFn();
        } catch (e) {
          console.error('[TrackingManager] Error in tracking call:', e);
        }
      }
    }
  };

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TrackingManager.init());
  } else {
    TrackingManager.init();
  }

  // Expose to global scope
  window.TrackingManager = TrackingManager;
})();
